import { cliSimpleGit } from '../cli-simple-git.js';
import { parseLogShaDateSubjectLines } from './lib/parse-log-lines.js';
import { sanitizeRecentCommitsLimit } from './lib/sanitize-recent-commits-limit.js';
import { listUnpushedCommits, type UnpushedCommitRef } from './list-unpushed-commits.js';

export {
  RECENT_COMMITS_LIMIT_DEFAULT,
  RECENT_COMMITS_LIMIT_MAX,
  RECENT_COMMITS_LIMIT_MIN,
  sanitizeRecentCommitsLimit,
} from './lib/sanitize-recent-commits-limit.js';

/**
 * Recent commits on the current branch (newest first), without push state.
 */
export async function listRecentCommitRefs(
  repoDir: string,
  limitInput: unknown,
): Promise<{
  commits: Array<{ sha: string; shortSha: string; subject: string; committedAt: string }>;
  hasMore: boolean;
}> {
  const limit = sanitizeRecentCommitsLimit(limitInput);
  const g = cliSimpleGit(repoDir);

  try {
    const logOut = await g.raw(['log', '--format=%H\t%cI\t%s', `-n`, String(limit + 1), 'HEAD']);
    const parsed = parseLogShaDateSubjectLines(logOut);
    const hasMore = parsed.length > limit;
    const commits = parsed.slice(0, limit);
    return { commits, hasMore };
  } catch {
    return { commits: [], hasMore: false };
  }
}

/**
 * Recent commits on the current branch (newest first), with whether each is not yet on the remote.
 */
export async function listRecentCommits(
  repoDir: string,
  limitInput: unknown,
  /** When set, skips a separate unpushed lookup (caller already fetched it). */
  unpushedCommitsInput?: readonly UnpushedCommitRef[],
): Promise<{
  commits: Array<{ sha: string; shortSha: string; subject: string; committedAt: string; needsPush: boolean }>;
  hasMore: boolean;
}> {
  let unpushedSet: Set<string>;
  if (unpushedCommitsInput) {
    unpushedSet = new Set(unpushedCommitsInput.map((c) => c.sha));
  } else {
    unpushedSet = new Set((await listUnpushedCommits(repoDir)).map((c) => c.sha));
  }

  const recentCommitList = await listRecentCommitRefs(repoDir, limitInput);
  const commits = recentCommitList.commits.map((c) => ({
    ...c,
    needsPush: unpushedSet.has(c.sha),
  }));
  return { commits, hasMore: recentCommitList.hasMore };
}
